import CopilotWidget from '../components/CopilotWidget'
/**
 * CoConnect — Enterprise MEL Dashboard (10/10)
 * ──────────────────────────────────────────────────
 * Monitoring, Evaluation & Learning dashboard with:
 *
 *   6 tabs: Overview | Financial | Operations | Compliance | Analytics | Pest/Disease
 *
 *   - 6 KPI tiles with trend indicators
 *   - Financial: payouts, payments, withdrawals, price/kg trends
 *   - Operations: batches by status, dispatch performance, truck fleet health
 *   - Compliance: EUDR rate, KYC verification, carbon credits
 *   - Analytics: adoption chart, quality distribution, sustainability gauge
 *   - Pest/Disease: AI pest scanner analytics from farmer reports
 *   - Live system health (API, DB, Redis, Security, Spatial OS)
 *   - CSV export with date range filter
 *   - AI MEL analyst (button to get AI insights)
 *   - Auto-refresh every 5 minutes
 *   - Loading skeletons + error states
 *   - Full mobile responsive with bottom nav
 *   - Navigation back to admin portal
 */
import LogoutButton from "../components/LogoutButton";
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { aiApi, type PestStats as PestStatsType } from '../services/aiApi';
import {
  BrandMark, Card, SectionHeader, MetricTile, StatusPill, SmartStatus,
  Spinner, EmptyState, Alert, IconButton, ProgressBar, Skeleton,
  SegmentedTabs, Pagination, usePagination,
} from '../components/ui';
import AdoptionChart from '../components/charts/AdoptionChart';
import QualityChart from '../components/charts/QualityChart';
import SustainabilityGauge from '../components/charts/SustainabilityGauge';
import { useAuth } from '../contexts/AuthContext'; // v3.2

// ─── Inline icons ──────────────────────────────────────────────
const Icon = {
  Users: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 11-8 0 4 4 0 018 0zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>),
  Package: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" /></svg>),
  Clock: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>),
  Wallet: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 12V7H5a2 2 0 010-4h14v4M3 5v14a2 2 0 002 2h16v-5M18 12a2 2 0 000 4h4v-4z" /></svg>),
  TrendingUp: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 6l-9.5 9.5-5-5L1 18M17 6h6v6" /></svg>),
  Leaf: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19.5 2c-.5 4.5-.5 7.5-3 12-2 3.5-7 4-7 4zM2 21c0-3 1.85-5.36 5.08-6" /></svg>),
  Refresh: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  Download: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>),
  BarChart: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 3v18h18M7 16V8M12 16v-5M17 16v-3" /></svg>),
  Server: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" /><path d="M6 6h.01M6 18h.01" /></svg>),
  Activity: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>),
  Database: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>),
  Zap: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>),
  Shield: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>),
  Truck: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>),
  Send: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>),
  Dollar: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>),
  Award: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="8" r="6" /><path d="M15.5 13.5L17 22l-5-3-5 3 1.5-8.5" /></svg>),
  Check: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M20 6L9 17l-5-5" /></svg>),
  Alert: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" /></svg>),
  Cpu: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" /></svg>),
};

interface DashboardMetrics {
  total_farmers: string;
  total_delivered: string;
  total_pending: string;
  total_payout: string;
  avg_price_per_kg: string;
  avg_zero_waste_score: string;
}

const MELDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // v3.2: dynamic userRole for CopilotWidget
  const [activeTab, setActiveTab] = useState<'overview' | 'financial' | 'operations' | 'compliance' | 'analytics' | 'pest'>('overview');
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [adoptionData, setAdoptionData] = useState<any[]>([]);
  const [qualityData, setQualityData] = useState<any[]>([]);
  const [sustainabilityData, setSustainabilityData] = useState<any>({});
  const [eudrData, setEudrData] = useState<any>(null);
  const [farmers, setFarmers] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [withdrawals, setWithdrawals] = useState<any[]>([]);
  const [dispatches, setDispatches] = useState<any[]>([]);
  const [trucks, setTrucks] = useState<any[]>([]);
  const [spatialHealth, setSpatialHealth] = useState<any>(null);
  const [pestStats, setPestStats] = useState<PestStatsType | null>(null);
  const [aiInsights, setAiInsights] = useState<any>(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  // v2.1.1 (BUG#4 fix): server only accepts '7d' | '30d' | '90d' | '365d'. '1y' was a 400.
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '365d'>('30d');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [
        dashboardRes, adoptionRes, qualityRes, sustainRes, eudrRes,
        farmersRes, batchesRes, txRes, wdRes, dispRes, trucksRes, spatialRes,
        pestStatsRes,
      ] = await Promise.all([
        apiClient.get('/mel/dashboard').catch(() => ({ data: {} })),
        apiClient.get(`/mel/adoption?period=${timeRange}`).catch(() => ({ data: [] })),
        apiClient.get('/mel/quality').catch(() => ({ data: [] })),
        apiClient.get('/mel/sustainability').catch(() => ({ data: {} })),
        apiClient.get('/mel/eudr').catch(() => ({ data: {} })),
        apiClient.get('/farmers').catch(() => ({ data: [] })),
        apiClient.get('/batches').catch(() => ({ data: [] })),
        apiClient.get('/transactions').catch(() => ({ data: [] })),
        apiClient.get('/withdrawals').catch(() => ({ data: [] })),
        apiClient.get('/dispatches').catch(() => ({ data: [] })),
        apiClient.get('/truck-portal/trucks').catch(() => ({ data: { trucks: [] } })),
        apiClient.get('/spatial/health').catch(() => ({ data: null })),
        aiApi.getPestStats().catch(() => null),
      ]);
      setMetrics(dashboardRes.data);
      setAdoptionData(adoptionRes.data);
      setQualityData(qualityRes.data);
      setSustainabilityData(sustainRes.data);
      setEudrData(eudrRes.data);
      setFarmers(Array.isArray(farmersRes.data) ? farmersRes.data : []);
      setBatches(Array.isArray(batchesRes.data) ? batchesRes.data : (batchesRes.data?.batches || []));
      setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
      setWithdrawals(Array.isArray(wdRes.data) ? wdRes.data : []);
      setDispatches(Array.isArray(dispRes.data) ? dispRes.data : []);
      setTrucks(trucksRes.data?.trucks || []);
      setSpatialHealth(spatialRes.data);
      setPestStats(pestStatsRes);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load dashboard data. Please refresh or try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formattedMetrics = useMemo(() => {
    if (!metrics) return null;
    return {
      totalFarmers: parseInt(metrics.total_farmers) || 0,
      totalDelivered: parseInt(metrics.total_delivered) || 0,
      totalPending: parseInt(metrics.total_pending) || 0,
      totalPayout: parseFloat(metrics.total_payout) || 0,
      avgPrice: parseFloat(metrics.avg_price_per_kg) || 0,
      avgZeroWaste: parseFloat(metrics.avg_zero_waste_score) || 0,
    };
  }, [metrics]);

  // ─── Derived analytics ─────────────────────────────────────────
  const derivedStats = useMemo(() => {
    const kycVerified = farmers.filter(f => f.kyc_verified).length;
    const kycRate = farmers.length > 0 ? (kycVerified / farmers.length) * 100 : 0;
    const totalEarnings = farmers.reduce((sum, f) => sum + (f.total_earnings || 0), 0);
    const totalBalance = farmers.reduce((sum, f) => sum + (f.available_balance || 0), 0);

    const batchByStatus: Record<string, number> = {};
    batches.forEach(b => { batchByStatus[b.status] = (batchByStatus[b.status] || 0) + 1; });

    const qualityByGrade: Record<string, number> = {};
    batches.forEach(b => { if (b.quality_grade) qualityByGrade[b.quality_grade] = (qualityByGrade[b.quality_grade] || 0) + 1; });

    const totalTxAmount = transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const pendingWithdrawals = withdrawals.filter(w => w.status === 'PENDING' || w.status === 'PROCESSING');
    const pendingWdAmount = pendingWithdrawals.reduce((sum, w) => sum + (w.amount || 0), 0);
    const completedWd = withdrawals.filter(w => w.status === 'COMPLETED');
    const completedWdAmount = completedWd.reduce((sum, w) => sum + (w.amount || 0), 0);

    const activeDispatches = dispatches.filter(d => d.status !== 'DELIVERED' && d.status !== 'CANCELLED');
    const deliveredDispatches = dispatches.filter(d => d.status === 'DELIVERED');
    const deliveryRate = dispatches.length > 0 ? (deliveredDispatches.length / dispatches.length) * 100 : 0;

    const availableTrucks = trucks.filter(t => t.status === 'AVAILABLE').length;
    const inTransitTrucks = trucks.filter(t => t.status === 'IN_TRANSIT').length;
    const maintenanceTrucks = trucks.filter(t => t.status === 'MAINTENANCE').length;
    const lowFuelTrucks = trucks.filter(t => t.fuel_level_pct < 20).length;

    const eudrComplianceRate = eudrData?.stats?.compliance_rate || 0;
    const eudrCompliant = eudrData?.stats?.compliant_batches || 0;
    const eudrNonCompliant = eudrData?.stats?.non_compliant_batches || 0;

    return {
      kycVerified, kycRate, totalEarnings, totalBalance,
      batchByStatus, qualityByGrade,
      totalTxAmount, pendingWithdrawals: pendingWithdrawals.length, pendingWdAmount,
      completedWd: completedWd.length, completedWdAmount,
      activeDispatches: activeDispatches.length, deliveredDispatches: deliveredDispatches.length,
      deliveryRate,
      availableTrucks, inTransitTrucks, maintenanceTrucks, lowFuelTrucks,
      eudrComplianceRate, eudrCompliant, eudrNonCompliant,
    };
  }, [farmers, batches, transactions, withdrawals, dispatches, trucks, eudrData]);

  const exportData = useCallback(() => {
    if (!metrics) return;
    const csv = [
      ['CoConnect — MEL Dashboard Export'],
      ['Generated', new Date().toISOString()],
      ['Time Range', timeRange],
      [],
      ['── Core Metrics ──'],
      ['Total Farmers', metrics.total_farmers],
      ['Total Delivered Batches', metrics.total_delivered],
      ['Total Pending Batches', metrics.total_pending],
      ['Total Payout (XAF)', metrics.total_payout],
      ['Avg Price/kg (XAF)', metrics.avg_price_per_kg],
      ['Avg Zero-Waste Score (%)', metrics.avg_zero_waste_score],
      [],
      ['── Farmer Analytics ──'],
      ['KYC Verified', derivedStats.kycVerified],
      ['KYC Rate (%)', derivedStats.kycRate.toFixed(1)],
      ['Total Farmer Earnings (XAF)', derivedStats.totalEarnings],
      ['Total Available Balance (XAF)', derivedStats.totalBalance],
      [],
      ['── Financial ──'],
      ['Total Transaction Amount (XAF)', derivedStats.totalTxAmount],
      ['Pending Withdrawals', derivedStats.pendingWithdrawals],
      ['Pending Withdrawal Amount (XAF)', derivedStats.pendingWdAmount],
      ['Completed Withdrawals', derivedStats.completedWd],
      ['Completed Withdrawal Amount (XAF)', derivedStats.completedWdAmount],
      [],
      ['── Operations ──'],
      ['Active Dispatches', derivedStats.activeDispatches],
      ['Delivered Dispatches', derivedStats.deliveredDispatches],
      ['Delivery Rate (%)', derivedStats.deliveryRate.toFixed(1)],
      ['Available Trucks', derivedStats.availableTrucks],
      ['In-Transit Trucks', derivedStats.inTransitTrucks],
      ['Maintenance Trucks', derivedStats.maintenanceTrucks],
      ['Low-Fuel Trucks (<20%)', derivedStats.lowFuelTrucks],
      [],
      ['── EUDR Compliance ──'],
      ['Compliant Batches', derivedStats.eudrCompliant],
      ['Non-Compliant Batches', derivedStats.eudrNonCompliant],
      ['Compliance Rate (%)', derivedStats.eudrComplianceRate],
    ].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mel-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, [metrics, timeRange, derivedStats]);

  // ─── Tabs ──────────────────────────────────────────────────────
  const tabs = [
    { id: 'overview',    label: 'Overview',    icon: <Icon.BarChart className="w-4 h-4" /> },
    { id: 'financial',   label: 'Financial',   icon: <Icon.Dollar className="w-4 h-4" /> },
    { id: 'operations',  label: 'Operations',  icon: <Icon.Truck className="w-4 h-4" /> },
    { id: 'compliance',  label: 'Compliance',  icon: <Icon.Shield className="w-4 h-4" /> },
    { id: 'analytics',   label: 'Analytics',   icon: <Icon.Activity className="w-4 h-4" /> },
    { id: 'pest',        label: 'Pest/Disease',icon: <Icon.Leaf className="w-4 h-4" />, badge: pestStats?.total || undefined },
  ];

  if (error && !metrics) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center p-5 safe-top">
        <Card padding="lg" className="max-w-md text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-danger-50 dark:bg-danger-500/10 flex items-center justify-center text-danger-600 dark:text-danger-500">
            <Icon.Activity className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Unable to load dashboard</h2>
          <p className="mt-1.5 text-sm text-ink-500 dark:text-ink-400">{error}</p>
          <button onClick={fetchData} className="mt-5 btn-primary">
            <Icon.Refresh className="w-4 h-4" /> Try again
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* ─── Top bar ─── */}
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="analytics" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate flex items-center gap-2">
                MEL Dashboard
                <span className="hidden md:inline-flex pill-success">
                  <span className="live-dot" /> Live
                </span>
              </p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate nums">
                Updated {lastUpdated.toLocaleTimeString()} · {farmers.length} farmers · {batches.length} batches
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value as any)}
              className="input py-1.5 text-xs w-auto hide-mobile">
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="365d">Last year</option>
            </select>
            <IconButton label="Refresh" onClick={fetchData} disabled={refreshing}>
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            <button onClick={exportData} className="btn-secondary text-xs hide-mobile">
              <Icon.Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button onClick={() => navigate('/admin')} className="btn-secondary text-xs hide-mobile">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              Admin
            </button>
            <LogoutButton />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-6">
        {error && <div className="mb-4"><Alert tone="warning">{error}</Alert></div>}

        {/* ─── Tabs (desktop) ─── */}
        <div className="hidden md:block mb-6">
          <SegmentedTabs items={tabs} active={activeTab} onChange={(id) => setActiveTab(id as any)} />
        </div>

        {/* ─── Mobile tab strip ─── */}
        <div className="md:hidden mb-5 -mx-4 px-4 overflow-x-auto no-scrollbar">
          <div className="inline-flex gap-1.5 pb-1">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id as any)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeTab === t.id ? 'bg-ink-900 dark:bg-ink-50 text-ink-50 dark:text-ink-900' : 'bg-ink-0 dark:bg-ink-850 text-ink-600 dark:text-ink-300 border border-ink-200 dark:border-ink-800'
                }`}>
                {t.icon}<span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ═══ OVERVIEW ═══ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI grid */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} padding="md">
                    <Skeleton className="h-3 w-16 mb-3" />
                    <Skeleton className="h-7 w-20 mb-2" />
                    <Skeleton className="h-2 w-12" />
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <MetricTile label="Farmers"        value={formattedMetrics?.totalFarmers || 0}                              icon={<Icon.Users className="w-4 h-4" />} accent="analytics" />
                <MetricTile label="Delivered"      value={formattedMetrics?.totalDelivered || 0}                           icon={<Icon.Package className="w-4 h-4" />} />
                <MetricTile label="Pending"        value={formattedMetrics?.totalPending || 0}                            icon={<Icon.Clock className="w-4 h-4" />} />
                <MetricTile label="Payout (XAF)"   value={(formattedMetrics?.totalPayout || 0).toLocaleString()}           icon={<Icon.Wallet className="w-4 h-4" />} />
                <MetricTile label="Avg price/kg"   value={`${(formattedMetrics?.avgPrice || 0).toFixed(0)} XAF`}           icon={<Icon.TrendingUp className="w-4 h-4" />} />
                <MetricTile label="Zero-waste"     value={`${(formattedMetrics?.avgZeroWaste || 0).toFixed(1)}%`}          icon={<Icon.Leaf className="w-4 h-4" />} />
              </div>
            )}

            {/* Quick summary cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card padding="lg">
                <SectionHeader eyebrow="Adoption" title="Farmer adoption" subtitle="New registrations over time." icon={<Icon.BarChart className="w-4 h-4" />} />
                <div className="mt-4"><AdoptionChart data={adoptionData} loading={loading} /></div>
              </Card>
              <Card padding="lg">
                <SectionHeader eyebrow="Quality" title="Quality grades" subtitle="Batch quality distribution." icon={<Icon.Package className="w-4 h-4" />} />
                <div className="mt-4"><QualityChart data={qualityData} loading={loading} /></div>
              </Card>
              <Card padding="lg">
                <SectionHeader eyebrow="Sustainability" title="Sustainability gauge" subtitle="Environmental performance." icon={<Icon.Leaf className="w-4 h-4" />} />
                <div className="mt-4"><SustainabilityGauge data={sustainabilityData} loading={loading} /></div>
              </Card>
            </div>

            {/* System health */}
            <Card padding="lg">
              <SectionHeader eyebrow="Telemetry" title="System health" subtitle="Real-time infrastructure status." icon={<Icon.Server className="w-4 h-4" />} />
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {[
                  { label: 'API status',     value: 'Operational',   icon: <Icon.Activity className="w-3.5 h-3.5" /> },
                  { label: 'Database',       value: 'Connected',     icon: <Icon.Database className="w-3.5 h-3.5" /> },
                  { label: 'Cache',          value: 'Redis active',  icon: <Icon.Zap className="w-3.5 h-3.5" /> },
                  { label: 'Security',       value: 'All secure',    icon: <Icon.Shield className="w-3.5 h-3.5" /> },
                  { label: 'Spatial OS',     value: spatialHealth ? `${spatialHealth.subscribers} subs` : 'Not configured', icon: <Icon.Cpu className="w-3.5 h-3.5" /> },
                  { label: 'SSE clients',    value: spatialHealth ? `${spatialHealth.sseClients} live` : '—', icon: <Icon.Activity className="w-3.5 h-3.5" /> },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2.5 surface-inset rounded-lg">
                    <span className="flex items-center gap-2 text-sm text-ink-600 dark:text-ink-300">
                      <span className="text-ink-400">{item.icon}</span>
                      {item.label}
                    </span>
                    <span className="flex items-center gap-2 text-sm font-medium text-ink-900 dark:text-ink-50">
                      <span className="live-dot" />
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* ═══ FINANCIAL ═══ */}
        {activeTab === 'financial' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Total payout"       value={`${(formattedMetrics?.totalPayout || 0).toLocaleString()} XAF`} icon={<Icon.Wallet className="w-4 h-4" />} accent="analytics" />
              <MetricTile label="Avg price/kg"       value={`${(formattedMetrics?.avgPrice || 0).toFixed(0)} XAF`}           icon={<Icon.Dollar className="w-4 h-4" />} />
              <MetricTile label="Pending withdrawals" value={derivedStats.pendingWithdrawals}                                icon={<Icon.Clock className="w-4 h-4" />} />
              <MetricTile label="Pending Wd amount"   value={`${derivedStats.pendingWdAmount.toLocaleString()} XAF`}          icon={<Icon.Alert className="w-4 h-4" />} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card padding="lg">
                <SectionHeader eyebrow="Payouts" title="Total payout distribution" icon={<Icon.Dollar className="w-4 h-4" />} />
                <div className="mt-4 space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-ink-500 dark:text-ink-400">Total payout</span>
                      <span className="font-semibold nums text-ink-900 dark:text-ink-50">{(formattedMetrics?.totalPayout || 0).toLocaleString()} XAF</span>
                    </div>
                    <ProgressBar value={100} />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-ink-500 dark:text-ink-400">Total earnings (farmers)</span>
                      <span className="font-semibold nums text-ink-900 dark:text-ink-50">{derivedStats.totalEarnings.toLocaleString()} XAF</span>
                    </div>
                    <ProgressBar value={formattedMetrics?.totalPayout ? Math.min(100, (derivedStats.totalEarnings / (formattedMetrics.totalPayout || 1)) * 100) : 0} tone="success" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-ink-500 dark:text-ink-400">Available balance (unpaid)</span>
                      <span className="font-semibold nums text-ink-900 dark:text-ink-50">{derivedStats.totalBalance.toLocaleString()} XAF</span>
                    </div>
                    <ProgressBar value={derivedStats.totalEarnings > 0 ? (derivedStats.totalBalance / derivedStats.totalEarnings) * 100 : 0} tone="warning" />
                  </div>
                </div>
              </Card>

              <Card padding="lg">
                <SectionHeader eyebrow="Withdrawals" title="Withdrawal status" icon={<Icon.Wallet className="w-4 h-4" />} />
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between p-3 surface-inset rounded-lg">
                    <div className="flex items-center gap-2">
                      <StatusPill tone="warning" dot>Pending</StatusPill>
                      <span className="text-sm text-ink-600 dark:text-ink-300">{derivedStats.pendingWithdrawals} requests</span>
                    </div>
                    <span className="text-sm font-bold nums text-ink-900 dark:text-ink-50">{derivedStats.pendingWdAmount.toLocaleString()} XAF</span>
                  </div>
                  <div className="flex items-center justify-between p-3 surface-inset rounded-lg">
                    <div className="flex items-center gap-2">
                      <StatusPill tone="success" dot>Completed</StatusPill>
                      <span className="text-sm text-ink-600 dark:text-ink-300">{derivedStats.completedWd} completed</span>
                    </div>
                    <span className="text-sm font-bold nums text-ink-900 dark:text-ink-50">{derivedStats.completedWdAmount.toLocaleString()} XAF</span>
                  </div>
                  <div className="flex items-center justify-between p-3 surface-inset rounded-lg">
                    <div className="flex items-center gap-2">
                      <StatusPill tone="info" dot>Transactions</StatusPill>
                      <span className="text-sm text-ink-600 dark:text-ink-300">{transactions.length} total</span>
                    </div>
                    <span className="text-sm font-bold nums text-ink-900 dark:text-ink-50">{derivedStats.totalTxAmount.toLocaleString()} XAF</span>
                  </div>
                </div>
              </Card>
            </div>

            <Card padding="lg">
              <SectionHeader eyebrow="Pricing" title="Price per kilogram" subtitle="Current buy price for cocoa beans." icon={<Icon.TrendingUp className="w-4 h-4" />} />
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-4xl font-semibold nums text-ink-900 dark:text-ink-50 tracking-tight">
                    {(formattedMetrics?.avgPrice || 0).toFixed(0)} <span className="text-lg text-ink-500 dark:text-ink-400">XAF/kg</span>
                  </p>
                  <p className="text-xs text-ink-500 dark:text-ink-400 mt-1">Average across all batches</p>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-analytics-50 dark:bg-analytics-500/10 flex items-center justify-center text-analytics-700 dark:text-analytics-500">
                  <Icon.TrendingUp className="w-8 h-8" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ OPERATIONS ═══ */}
        {activeTab === 'operations' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Total batches"      value={batches.length}                              icon={<Icon.Package className="w-4 h-4" />} accent="analytics" />
              <MetricTile label="Active dispatches"   value={derivedStats.activeDispatches}               icon={<Icon.Send className="w-4 h-4" />} />
              <MetricTile label="Delivered"           value={derivedStats.deliveredDispatches}            icon={<Icon.Check className="w-4 h-4" />} />
              <MetricTile label="Delivery rate"       value={`${derivedStats.deliveryRate.toFixed(0)}%`}   icon={<Icon.TrendingUp className="w-4 h-4" />} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Batch status breakdown */}
              <Card padding="lg">
                <SectionHeader eyebrow="Batches" title="Batch status breakdown" icon={<Icon.Package className="w-4 h-4" />} />
                <div className="mt-4 space-y-2">
                  {Object.entries(derivedStats.batchByStatus).length > 0 ? (
                    Object.entries(derivedStats.batchByStatus)
                      .sort((a, b) => b[1] - a[1])
                      .map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between p-2.5 surface-inset rounded-lg">
                          <SmartStatus status={status} />
                          <span className="text-sm font-bold nums text-ink-900 dark:text-ink-50">{count}</span>
                        </div>
                      ))
                  ) : (
                    <EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No batches" description="Batch data will appear here." />
                  )}
                </div>
              </Card>

              {/* Quality distribution */}
              <Card padding="lg">
                <SectionHeader eyebrow="Quality" title="Quality grade distribution" icon={<Icon.Award className="w-4 h-4" />} />
                <div className="mt-4 space-y-2">
                  {Object.entries(derivedStats.qualityByGrade).length > 0 ? (
                    Object.entries(derivedStats.qualityByGrade)
                      .sort((a, b) => a[0].localeCompare(b[0]))
                      .map(([grade, count]) => (
                        <div key={grade} className="flex items-center justify-between p-2.5 surface-inset rounded-lg">
                          <SmartStatus status={grade} />
                          <span className="text-sm font-bold nums text-ink-900 dark:text-ink-50">{count}</span>
                        </div>
                      ))
                  ) : (
                    <EmptyState icon={<Icon.Award className="w-5 h-5" />} title="No quality data" description="Quality grades will appear here." />
                  )}
                </div>
              </Card>
            </div>

            {/* Fleet status */}
            <Card padding="lg">
              <SectionHeader eyebrow="Fleet" title="Truck fleet status" subtitle={`${trucks.length} trucks registered`} icon={<Icon.Truck className="w-4 h-4" />} />
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-4 surface-inset rounded-lg">
                  <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500">Available</p>
                  <p className="text-2xl font-bold nums text-success-600 dark:text-success-500 mt-1">{derivedStats.availableTrucks}</p>
                </div>
                <div className="p-4 surface-inset rounded-lg">
                  <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500">In transit</p>
                  <p className="text-2xl font-bold nums text-info-600 dark:text-info-500 mt-1">{derivedStats.inTransitTrucks}</p>
                </div>
                <div className="p-4 surface-inset rounded-lg">
                  <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500">Maintenance</p>
                  <p className="text-2xl font-bold nums text-warning-600 dark:text-warning-500 mt-1">{derivedStats.maintenanceTrucks}</p>
                </div>
                <div className="p-4 surface-inset rounded-lg">
                  <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500">Low fuel (&lt;20%)</p>
                  <p className="text-2xl font-bold nums text-danger-600 dark:text-danger-500 mt-1">{derivedStats.lowFuelTrucks}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ COMPLIANCE ═══ */}
        {activeTab === 'compliance' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="EUDR compliant"     value={derivedStats.eudrCompliant}                       icon={<Icon.Shield className="w-4 h-4" />} accent="analytics" />
              <MetricTile label="Non-compliant"      value={derivedStats.eudrNonCompliant}                    icon={<Icon.Alert className="w-4 h-4" />} />
              <MetricTile label="Compliance rate"    value={`${derivedStats.eudrComplianceRate}%`}            icon={<Icon.Check className="w-4 h-4" />} />
              <MetricTile label="KYC verified"       value={`${derivedStats.kycRate.toFixed(0)}%`}            icon={<Icon.Users className="w-4 h-4" />} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* EUDR compliance */}
              <Card padding="lg">
                <SectionHeader eyebrow="EUDR" title="EUDR compliance status" subtitle="EU Deforestation Regulation readiness." icon={<Icon.Shield className="w-4 h-4" />} />
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-ink-500 dark:text-ink-400">Compliance rate</span>
                    <span className="text-2xl font-bold nums text-ink-900 dark:text-ink-50">{derivedStats.eudrComplianceRate}%</span>
                  </div>
                  <ProgressBar
                    value={derivedStats.eudrComplianceRate}
                    tone={derivedStats.eudrComplianceRate >= 80 ? 'success' : derivedStats.eudrComplianceRate >= 50 ? 'warning' : 'danger'}
                  />
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="p-3 bg-success-50 dark:bg-success-500/10 rounded-lg border border-success-500/20">
                      <p className="text-2xs uppercase tracking-micro text-success-700 dark:text-success-500">Compliant</p>
                      <p className="text-xl font-bold nums text-success-700 dark:text-success-500 mt-1">{derivedStats.eudrCompliant}</p>
                    </div>
                    <div className="p-3 bg-warning-50 dark:bg-warning-500/10 rounded-lg border border-warning-500/20">
                      <p className="text-2xs uppercase tracking-micro text-warning-700 dark:text-warning-500">Pending</p>
                      <p className="text-xl font-bold nums text-warning-700 dark:text-warning-500 mt-1">{derivedStats.eudrNonCompliant}</p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* KYC verification */}
              <Card padding="lg">
                <SectionHeader eyebrow="Identity" title="KYC verification" subtitle="Farmer identity verification rate." icon={<Icon.Users className="w-4 h-4" />} />
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-ink-500 dark:text-ink-400">Verification rate</span>
                    <span className="text-2xl font-bold nums text-ink-900 dark:text-ink-50">{derivedStats.kycRate.toFixed(1)}%</span>
                  </div>
                  <ProgressBar
                    value={derivedStats.kycRate}
                    tone={derivedStats.kycRate >= 80 ? 'success' : derivedStats.kycRate >= 50 ? 'warning' : 'danger'}
                  />
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="p-3 bg-success-50 dark:bg-success-500/10 rounded-lg border border-success-500/20">
                      <p className="text-2xs uppercase tracking-micro text-success-700 dark:text-success-500">Verified</p>
                      <p className="text-xl font-bold nums text-success-700 dark:text-success-500 mt-1">{derivedStats.kycVerified}</p>
                    </div>
                    <div className="p-3 bg-warning-50 dark:bg-warning-500/10 rounded-lg border border-warning-500/20">
                      <p className="text-2xs uppercase tracking-micro text-warning-700 dark:text-warning-500">Pending</p>
                      <p className="text-xl font-bold nums text-warning-700 dark:text-warning-500 mt-1">{farmers.length - derivedStats.kycVerified}</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Zero-waste */}
            <Card padding="lg">
              <SectionHeader eyebrow="Sustainability" title="Zero-waste score" subtitle="Average sustainability performance across all batches." icon={<Icon.Leaf className="w-4 h-4" />} />
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-4xl font-semibold nums text-ink-900 dark:text-ink-50 tracking-tight">
                    {(formattedMetrics?.avgZeroWaste || 0).toFixed(1)}<span className="text-lg text-ink-500 dark:text-ink-400">%</span>
                  </p>
                  <p className="text-xs text-ink-500 dark:text-ink-400 mt-1">
                    {(formattedMetrics?.avgZeroWaste || 0) >= 80 ? 'Excellent — meeting sustainability targets' : (formattedMetrics?.avgZeroWaste || 0) >= 60 ? 'Good — room for improvement' : 'Needs attention — below target'}
                  </p>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-agrarian-50 dark:bg-agrarian-500/10 flex items-center justify-center text-agrarian-700 dark:text-agrarian-500">
                  <Icon.Leaf className="w-8 h-8" />
                </div>
              </div>
              <div className="mt-4">
                <ProgressBar
                  value={formattedMetrics?.avgZeroWaste || 0}
                  tone={(formattedMetrics?.avgZeroWaste || 0) >= 80 ? 'success' : (formattedMetrics?.avgZeroWaste || 0) >= 60 ? 'warning' : 'danger'}
                  showLabel
                />
              </div>
            </Card>
          </div>
        )}

        {/* ═══ ANALYTICS ═══ */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card padding="lg">
                <SectionHeader eyebrow="Adoption" title="Farmer adoption trend" subtitle="New farmer registrations over time." icon={<Icon.BarChart className="w-4 h-4" />} />
                <div className="mt-4"><AdoptionChart data={adoptionData} loading={loading} /></div>
              </Card>
              <Card padding="lg">
                <SectionHeader eyebrow="Quality" title="Quality grade distribution" subtitle="Batch quality across the season." icon={<Icon.Package className="w-4 h-4" />} />
                <div className="mt-4"><QualityChart data={qualityData} loading={loading} /></div>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card padding="lg">
                <SectionHeader eyebrow="Sustainability" title="Sustainability metrics" subtitle="Environmental performance indicators." icon={<Icon.Leaf className="w-4 h-4" />} />
                <div className="mt-4"><SustainabilityGauge data={sustainabilityData} loading={loading} /></div>
              </Card>

              <Card padding="lg">
                <SectionHeader eyebrow="Data" title="Raw metrics" subtitle="Debug data (dev mode only)." icon={<Icon.Database className="w-4 h-4" />} />
                <div className="mt-4 space-y-2">
                  {formattedMetrics && Object.entries(formattedMetrics).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between p-2 surface-inset rounded text-sm">
                      <span className="font-mono text-2xs text-ink-500 dark:text-ink-400">{k}</span>
                      <span className="font-semibold nums text-ink-900 dark:text-ink-50">{typeof v === 'number' ? v.toLocaleString() : v}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {import.meta.env.DEV && (
              <Card padding="md">
                <details>
                  <summary className="cursor-pointer text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 hover:text-ink-700 dark:hover:text-ink-300">
                    Debug: raw API response
                  </summary>
                  <pre className="mt-3 text-2xs text-ink-600 dark:text-ink-300 overflow-auto max-h-60 p-4 surface-inset rounded-lg nums">
                    {JSON.stringify(metrics, null, 2)}
                  </pre>
                </details>
              </Card>
            )}
          </div>
        )}

        {/* ═══ PEST/DISEASE ═══ */}
        {activeTab === 'pest' && (
          <div className="space-y-6">
            {pestStats ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricTile label="Total scans"     value={pestStats.total}     icon={<Icon.Leaf className="w-4 h-4" />} accent="analytics" />
                  <MetricTile label="Last 7 days"     value={pestStats.last7d}    icon={<Icon.Clock className="w-4 h-4" />} />
                  <MetricTile label="Unique diseases"  value={pestStats.byDiagnosis.length} icon={<Icon.Database className="w-4 h-4" />} />
                  <MetricTile label="Critical"        value={pestStats.bySeverity.find(s => s.severity === 'critical')?.count || 0} icon={<Icon.Shield className="w-4 h-4" />} />
                </div>

                {pestStats.byDiagnosis.length > 0 && (
                  <Card padding="lg">
                    <SectionHeader eyebrow="Distribution" title="Disease/pest breakdown" subtitle="All diagnoses from farmer AI scans." icon={<Icon.BarChart className="w-4 h-4" />} />
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {pestStats.byDiagnosis.map((d, i) => (
                        <div key={i} className="flex items-center justify-between p-2.5 surface-inset rounded-lg">
                          <span className="text-sm text-ink-700 dark:text-ink-200 truncate">{d.diagnosis}</span>
                          <span className="text-sm font-bold nums text-ink-900 dark:text-ink-50 shrink-0 ml-2">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {pestStats.bySeverity.length > 0 && (
                  <Card padding="lg">
                    <SectionHeader eyebrow="Impact" title="Severity distribution" icon={<Icon.Shield className="w-4 h-4" />} />
                    <div className="mt-4 space-y-2">
                      {pestStats.bySeverity.map((s, i) => {
                        const tone = s.severity === 'critical' || s.severity === 'high' ? 'danger' : s.severity === 'moderate' ? 'warning' : 'success';
                        return (
                          <div key={i} className="flex items-center gap-3 p-2.5 surface-inset rounded-lg">
                            <StatusPill tone={tone as any} dot>{s.severity}</StatusPill>
                            <div className="flex-1"><ProgressBar value={pestStats.total > 0 ? (s.count / pestStats.total) * 100 : 0} tone={tone as any} /></div>
                            <span className="text-sm font-bold nums text-ink-900 dark:text-ink-50 shrink-0">{s.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {pestStats.byStatus.length > 0 && (
                  <Card padding="lg">
                    <SectionHeader eyebrow="Workflow" title="Report status" icon={<Icon.Activity className="w-4 h-4" />} />
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                      {pestStats.byStatus.map((s, i) => (
                        <div key={i} className="p-3 surface-inset rounded-lg text-center">
                          <SmartStatus status={s.status} />
                          <p className="text-xl font-bold nums text-ink-900 dark:text-ink-50 mt-1">{s.count}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            ) : (
              <Card padding="lg">
                <EmptyState icon={<Icon.Leaf className="w-5 h-5" />} title="No pest data yet" description="When farmers scan their crops using the AI pest scanner, disease statistics will appear here." />
              </Card>
            )}

            {/* AI MEL Analyst */}
            <Card padding="lg">
              <SectionHeader eyebrow="AI insights" title="AI MEL analyst" subtitle="Get AI-powered insights and recommendations based on your dashboard data." icon={<Icon.Activity className="w-4 h-4" />} />
              <div className="mt-4">
                <button
                  onClick={async () => {
                    setAiAnalyzing(true);
                    // v2.1.1 (BUG#5 fix): surface AI failures to the user.
                    try {
                      const r = await aiApi.analyzeMEL(formattedMetrics);
                      setAiInsights(r);
                    } catch (aiErr: any) {
                      setError(aiErr?.response?.data?.error || aiErr?.message || 'AI analysis failed — please try again.');
                    }
                    setAiAnalyzing(false);
                  }}
                  disabled={aiAnalyzing}
                  className="btn-secondary"
                >
                  {aiAnalyzing ? <><Spinner size="sm" /> <span>Analyzing metrics…</span></> : <><Icon.Activity className="w-4 h-4" /> <span>Analyze with AI</span></>}
                </button>
                {aiInsights && (
                  <div className="mt-4 space-y-3">
                    <div className={`p-3 rounded-lg border ${
                      aiInsights.riskLevel === 'high' ? 'bg-danger-50 dark:bg-danger-500/10 border-danger-500/20'
                      : aiInsights.riskLevel === 'moderate' ? 'bg-warning-50 dark:bg-warning-500/10 border-warning-500/20'
                      : 'bg-success-50 dark:bg-success-500/10 border-success-500/20'
                    }`}>
                      <p className={`text-sm font-semibold capitalize ${
                        aiInsights.riskLevel === 'high' ? 'text-danger-700 dark:text-danger-500'
                        : aiInsights.riskLevel === 'moderate' ? 'text-warning-700 dark:text-warning-500'
                        : 'text-success-700 dark:text-success-500'
                      }`}>Risk level: {aiInsights.riskLevel}</p>
                    </div>
                    {aiInsights.insights?.map((ins: string, i: number) => (
                      <div key={i} className="p-2.5 surface-inset rounded-lg text-sm text-ink-700 dark:text-ink-200 text-pretty">{ins}</div>
                    ))}
                    {aiInsights.recommendations?.map((rec: string, i: number) => (
                      <div key={i} className="p-2.5 surface-inset rounded-lg text-sm text-ink-700 dark:text-ink-200 text-pretty">{rec}</div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </main>

      {/* ─── Bottom nav (mobile) ─── */}
      <nav className="bottomnav md:hidden">
        <div className="overflow-x-auto no-scrollbar">
          <div className="inline-flex gap-0 px-2" style={{ minWidth: '100%' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id as any)}
              className={`relative flex flex-col items-center justify-center gap-1 py-2.5 text-2xs font-medium transition-colors ${
                activeTab === t.id ? 'text-ink-900 dark:text-ink-50' : 'text-ink-500 dark:text-ink-400'
              }`}>
              {t.icon}
              <span className="whitespace-nowrap">{t.label}</span>
              {activeTab === t.id && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-analytics-700 rounded-full" />}
            </button>
          ))}
          </div>
        </div>
      </nav>
          <CopilotWidget userRole={(user as any)?.role || 'admin'} />
</div>
  );
};

export default MELDashboard;
